import { Add, Delete } from "@mui/icons-material";
import {
    Alert,
    Box, Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton, Menu, MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import CombinedPaymentsEditor from "../../components/checkout/CombinedPaymentsEditor";
import OrderStagesPanel from "../../components/order/OrderStagesPanel";
import SalesChannelSelect from "../../components/checkout/SalesChannelSelect";
import ShippingMethodsSelect from "../../components/checkout/ShippingMethodsSelect";
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import MoneyInput from "../../components/MoneyInput";
import MoneyInputWithBlur from "../../components/MoneyInputWithBlur";
import PhoneInput from "../../components/PhoneInput";
import {
    aggregatePaymentDateFromRows,
    buildPaymentMethodsPayload,
    createPaymentRow,
    paymentsFromApiMethods,
    syncSinglePaymentAmount,
    validatePaymentRows,
} from "../../helpers/checkoutPayments";
import { formatCurrencyInCents, formatPhone, resolvePublicMediaUrl } from "../../helpers/formatters";
import { applyName } from "../../helpers/captureMode";
import { ordersListReturnPath } from "../../helpers/orders";
import { externalOrderIdLabel, normalizeExternalOrderUrl } from "../../helpers/salesChannels";
import {
    channelForcesPaid,
    channelNeedsShipping,
    normalizeChannelKind,
    normalizeProductKind,
    productAllowedForChannel,
    productFilterHint,
} from "../../helpers/orderStages";

function parseShippingCostToCents(shippingCost) {
    if (shippingCost === '' || shippingCost == null) {
        return 0;
    }
    const n = Number(shippingCost);
    if (isNaN(n)) {
        return 0;
    }
    return Math.max(0, Math.round(n * 100));
}

function formatQuoteShippingOption(option) {
    const name = option?.name || 'Frete';
    const cost = Number(option?.cost) || 0;
    const days = Number(option?.delivery_days) || 0;
    const parts = [name, formatCurrencyInCents(cost)];
    if (days > 0) {
        parts.push(`${days} dia(s)`);
    }
    return parts.join(' — ');
}

function formatQuotePaymentOption(option) {
    const name = option?.name || 'Pagamento';
    const amount = Number(option?.amount) || 0;
    const installments = Number(option?.installments) || 1;
    const parts = [name];
    if (amount > 0) {
        parts.push(formatCurrencyInCents(amount));
    }
    if (installments > 1) {
        parts.push(`${installments}x`);
    }
    return parts.join(' — ');
}

function EditCheckoutPage() {
    const { id } = useParams(); // Pega o ID do checkout pela URL
    const [salesChannel, setSalesChannel] = useState('');
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [externalOrderId, setExternalOrderId] = useState('');
    const [externalOrderUrl, setExternalOrderUrl] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [productName, setProductName] = useState('');
    const [productUnitPrice, setProductUnitPrice] = useState('');
    const [productQuantity, setProductQuantity] = useState(1);
    const [productId, setProductId] = useState('');
    const [productKind, setProductKind] = useState('');
    const [payments, setPayments] = useState(() => [createPaymentRow()]);
    const [customerWillPayLater, setCustomerWillPayLater] = useState(false);
    const [shippingMethod, setShippingMethod] = useState('');
    const [shippingCost, setShippingCost] = useState('0');
    const [commission, setCommission] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [discountReason, setDiscountReason] = useState('');
    const [observations, setObservations] = useState('');
    const [shippingDeadline, setShippingDeadline] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [amount, setAmount] = useState(0);
    const [receivedAmount, setReceivedAmount] = useState(0);
    const [finishOrder, setFinishOrder] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    let responseReceived = false;
    const navigate = useNavigate();
    const location = useLocation();
    const returnParams = new URLSearchParams(location.search);
    const ordersReturnPath = ordersListReturnPath(returnParams.get('return_to')) || '/admin/orders';
    const [resultErrors, setResultErrors] = useState({
        has_errors: false,
        customer_name: null,
        sales_channel: null,
        products: null,
        shipping_method: null,
        payment_methods: null,
        discount_reason: null,
        external_order_id: null,
        external_order_url: null,
    });
    const [isLoadingCustomerSearch, setIsLoadingCustomerSearch] = useState(false);
    const timerRef = useRef(null); // Usado para armazenar o timer
    const [customerResults, setCustomerResults] = useState([]);
    const [anchorElCustomer, setAnchorElCustomer] = useState(null);
    const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(false);
    const [productResults, setProductResults] = useState([]);
    const [anchorElProduct, setAnchorElProduct] = useState(null);
    const [addProductErrors, setAddProductErrors] = useState({
        product_name: '',
        product_price: '',
        product_quantity: '',
    });
    const [checkoutImage, setCheckoutImage] = useState(null);
    const [useQuickCheckoutCreationDate, setUseQuickCheckoutCreationDate] = useState(false);
    /** Vindo da API — quick checkout não exige linhas de produto na edição. */
    const [isQuickCheckout, setIsQuickCheckout] = useState(false);
    const [fromQuote, setFromQuote] = useState(null);
    const [orderRecord, setOrderRecord] = useState(null);

    const productsTotal = products.reduce((sum, product) => sum + product.total, 0);
    const shippingCents = parseShippingCostToCents(shippingCost);
    const discountCents = Math.max(0, Math.round((Number(discount) || 0) * 100));
    const grossAmount = productsTotal + shippingCents;
    const payableAmount = Math.max(0, productsTotal - discountCents) + shippingCents;
    const effectiveGrossAmount = (isQuickCheckout && products.length === 0) ? amount : grossAmount;
    const effectivePayableAmount = (isQuickCheckout && products.length === 0)
        ? Math.max(0, amount - discountCents)
        : payableAmount;

    useEffect(() => {
        if (!id) {
            return;
        }
        setIsLoading(true);
        setDidNotLoad(false);
        api.get(`orders/${id}`)
                    .catch(() => api.get(`checkouts/${id}`))
                    .then((response) => {
                        const data = response.data.data;
                        setOrderRecord(data);
                        setIsQuickCheckout(Boolean(data.is_quick_checkout));
                        setUseQuickCheckoutCreationDate(false);
                        setFromQuote(data.from_quote || null);
                        setSalesChannel(data.sales_channel?.id || data.sales_channel?.uuid || '');
                        setCustomerName(data.customer?.name || '');
                        setCustomerPhone(data.customer?.phone || '');
                        setCustomerEmail(data.customer?.email || '');
                        setCustomerId(data.customer?.uuid || data.customer?.id || '');
                        if (data.products && data.products.length > 0) {
                            const updatedProducts = data.products.map((product) => {
                                const total = product.base_price * product.quantity;
                                return { ...product, total };
                            });
                            setProducts(updatedProducts || []);
                        } else {
                            setProducts([]);
                        }
                        try {
                            const fallbackDate = data.payment_details?.payment_date
                                ? new Date(data.payment_details.payment_date)
                                : new Date();
                            setPayments(paymentsFromApiMethods(data.payment_methods, {
                                totalAmountCents: data.amount || 0,
                                fallbackDate,
                            }));
                        } catch (ex) {
                            console.log(ex);
                            setPayments([createPaymentRow({ amountCents: data.amount || 0 })]);
                        }

                        setShippingMethod(data.shipping_method?.name || '');
                        try {
                            let currentShippingCost = data.shipping_method?.cost / 100;
                            if (isNaN(currentShippingCost)) {
                                currentShippingCost = 0;
                            }
                            setShippingCost(currentShippingCost.toString());
                        } catch (ex) {
                            setShippingCost('0');
                        }
                        setShippingDeadline(data.shipping_method?.deadline || '');
                        setCommission(((Number(data.commission) || 0) / 100));
                        setDiscount(((Number(data.discount) || 0) / 100));
                        setDiscountReason(data.discount_reason || '');
                        setObservations(data.observations || '');
                        setExternalOrderId(data.external_order_id || '');
                        setExternalOrderUrl(data.external_order_url || '');
                        setAmount(data.amount || 0);
                        setReceivedAmount(data.amount || 0);

                        let payLater = !data.payment_details.paid;
                        if (data.photo != null) {
                            payLater = false;
                        }
                        setCustomerWillPayLater(payLater);
                        if (payLater) {
                            setPayments([]);
                        }

                        // Caminho /images/... é relativo ao host da API; sem resolver, o <img> aponta para o dev server do React.
                        const rawPhoto = data.photo || data.photo_url || data.image || data.image_url || null;
                        setCheckoutImage(rawPhoto ? resolvePublicMediaUrl(rawPhoto) : null);
                        setUseQuickCheckoutCreationDate(true);
                    })
                    .catch((error) => {
                        console.log(error);
                        setDidNotLoad(true);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
    }, [id, navigate]);

    const handleCheckboxChange = (event) => {
        setCustomerWillPayLater(event.target.checked);
        if (event.target.checked) {
            setPayments([]);
        } else {
            setPayments([createPaymentRow({ amountCents: effectivePayableAmount, paymentDate: new Date() })]);
        }
    };

    useEffect(() => {
        if (customerWillPayLater || isLoading) {
            return;
        }
        setPayments((prev) => syncSinglePaymentAmount(prev, effectivePayableAmount));
    }, [effectivePayableAmount, customerWillPayLater, isLoading]);

    useEffect(() => {
        if (channelForcesPaid(selectedChannel?.kind)) {
            setCustomerWillPayLater(false);
            if (payments.length === 0) {
                setPayments([createPaymentRow({ amountCents: effectivePayableAmount, paymentDate: new Date() })]);
            }
        }
    }, [selectedChannel]);

    const handleUpdateAmount = () => {
        const shippingCostInCents = parseShippingCostToCents(shippingCost);
        const newAmount = products.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);
    };

    const handleAddProduct = () => {
        const unitPriceInCents = parseFloat(productUnitPrice.replace(/[^\d]/g, ""));
        const shippingCostInCents = parseShippingCostToCents(shippingCost);
        const currentQuantity = parseInt(productQuantity);

        let hasProductErrors = false;
        let currentProductErrors = {
            product_name: '',
            product_price: '',
            product_quantity: '',
        };

        console.log('productId', productId);
        console.log('productName', productName);
        if (productName === '' && productId === '') {
            console.log('travando no product_name');
            currentProductErrors.product_name = 'Informe o nome do produto';
            hasProductErrors = true;
        }

        console.log('unitPriceInCents', unitPriceInCents);
        if (isNaN(unitPriceInCents) || unitPriceInCents <= 0) {
            console.log('travando no product_price');
            currentProductErrors.product_price = 'Informe o valor do produto';
            hasProductErrors = true;
        }

        console.log('currentQuantity', currentQuantity);
        if (isNaN(currentQuantity) || currentQuantity <= 0) {
            console.log('travando no product_quantity');
            currentProductErrors.product_quantity = 'Deve ser informada a quantidade';
            hasProductErrors = true;
        }

        console.log('hasProductErrors', hasProductErrors);
        console.log('addProductErrors', addProductErrors);

        if (hasProductErrors) {
            setResultErrors({
                ...resultErrors,
                has_errors: true,
            });
            setAddProductErrors({
                ...addProductErrors,
                ...currentProductErrors,
            })
            return
        }

        setAddProductErrors({
            product_name: '',
            product_price: '',
            product_quantity: '',
        });
        setResultErrors({
            ...resultErrors,
            has_errors: false,
        });
        hasProductErrors = false;

        let product = {
            name: productName,
            base_price: unitPriceInCents,
            quantity: currentQuantity,
            total: unitPriceInCents * productQuantity,
            kind: normalizeProductKind(productKind),
        };
        if (productId !== '') {
            product.id = productId;
            setProductId('');
        }
        setProductKind('');

        const newProducts = [...products, product];
        setProducts(newProducts);

        const newAmount = newProducts.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);

        setProductName('');
        setProductUnitPrice('');
        setProductQuantity(1);
    };

    const handleRemoveProduct = (index) => {
        const shippingCostInCents = parseShippingCostToCents(shippingCost);
        const newProducts = products.filter((_, i) => i !== index);
        setProducts(newProducts);

        const newAmount = newProducts.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);
    };

    const handleSubmit = (event, options = {}) => {
        event.preventDefault();

        const finishOrderFlag = typeof options.finishOrder === 'boolean' ? options.finishOrder : finishOrder;

        let allRequiredFieldsOk = true;
        let invalidData = {};
        if (!isQuickCheckout && products.length === 0) {
            invalidData.products = 'Você deve incluir ao menos 1 produto. Informe o nome, valor unitário, quantidade e clique no botão "Incluir este produto" para adicionar.';
            allRequiredFieldsOk = false;
        }

        if (customerId === '' && customerName === '') {
            invalidData.customer_name = 'Informe o nome do cliente';
            allRequiredFieldsOk = false;
        }
        if (discountCents > 0 && String(discountReason || '').trim() === '') {
            invalidData.discount_reason = 'Informe o motivo do desconto.';
            allRequiredFieldsOk = false;
        }
        if (selectedChannel?.requires_external_order_id === true && String(externalOrderId || '').trim() === '') {
            invalidData.external_order_id = `Informe o ${externalOrderIdLabel(selectedChannel?.name)}.`;
            allRequiredFieldsOk = false;
        }
        const trimmedExternalUrl = String(externalOrderUrl || '').trim();
        if (trimmedExternalUrl && !normalizeExternalOrderUrl(trimmedExternalUrl)) {
            invalidData.external_order_url = 'Informe uma URL válida (http ou https).';
            allRequiredFieldsOk = false;
        }
        if (products.some((product) => product.kind && !productAllowedForChannel(selectedChannel?.kind, product.kind))) {
            invalidData.products = 'Este canal não aceita um ou mais produtos da cesta.';
            allRequiredFieldsOk = false;
        }
        if (!customerWillPayLater) {
            const paymentError = validatePaymentRows(payments, effectivePayableAmount);
            if (paymentError) {
                invalidData.payment_methods = paymentError;
                allRequiredFieldsOk = false;
            }
        }
        if (!allRequiredFieldsOk) {
            setResultErrors({
                ...resultErrors,
                ...invalidData,
                has_errors: true,
            });
            return
        }

        setIsSubmitting(true);

        const paymentMethods = customerWillPayLater ? [] : buildPaymentMethodsPayload(payments);
        const aggregatePaymentDate = customerWillPayLater
            ? null
            : aggregatePaymentDateFromRows(payments);

        const updatedCheckout = {
            customer: {
                id: customerId,
                name: customerName,
                phone: customerPhone,
                email: customerEmail,
            },
            shipping_method: {
                name: shippingMethod,
                cost: shippingCost * 100,
                deadline: parseInt(shippingDeadline),
            },
            products: products,
            amount: effectiveGrossAmount,
            discount: discountCents,
            discount_reason: discountCents > 0 ? String(discountReason || '').trim() : '',
            observations: String(observations || '').trim(),
            external_order_id: String(externalOrderId || '').trim(),
            external_order_url: normalizeExternalOrderUrl(externalOrderUrl) || String(externalOrderUrl || '').trim(),
            sales_channel: {
                id: salesChannel,
            },
            finish_order: true,
            commission: Math.max(0, Math.round((Number(commission) || 0) * 100)),
            payment_details: {
                paid: channelForcesPaid(selectedChannel?.kind) ? true : !customerWillPayLater,
            },
            use_quick_checkout_creation_date: useQuickCheckoutCreationDate,
        };
        if (paymentMethods.length > 0) {
            updatedCheckout.payment_methods = paymentMethods;
        }

        if (!customerWillPayLater && aggregatePaymentDate != null) {
            updatedCheckout.payment_details.payment_date = aggregatePaymentDate.toISOString();
        }

        const redirectTo = ordersReturnPath;

        api.put(`checkouts/${id}`, updatedCheckout)
            .then((response) => {
                responseReceived = true;
                try {
                    const data = response.data;
                    if (!data?.success) {
                        setResultErrors({
                            ...data.errors,
                            has_errors: true,
                        });
                    } else {
                        setFinishOrder(finishOrderFlag);
                        navigate(redirectTo);
                    }
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((error) => {
                responseReceived = true;
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!responseReceived) {
                    setDidNotLoad(true);
                }
            });
    };

    const customerSearch = (customerName) => {
        if (customerName.length >= 3) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                setIsLoadingCustomerSearch(true);
                api.get(`customers?q=${customerName}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.customers)
                            ? d.customers
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setCustomerResults(rows);

                        setIsLoadingCustomerSearch(false);
                    })
                    .catch((error) => {
                        console.error(error);
                        setCustomerResults([]); // Evita que o menu fique aberto com erro
                        setIsLoadingCustomerSearch(false);
                    });
            }, 1000);
        } else {
            setCustomerResults([]); // Limpa os resultados se o nome for menor que 3 caracteres
        }
    };

    const handleCloseCustomerMenu = () => {
        setAnchorElCustomer(null);
        setCustomerResults([]);
    };

    const productSearch = (productName) => {
        if (productName.length >= 3) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                setIsLoadingProductSearch(true);
                api.get(`products?q=${productName}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.products)
                            ? d.products
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        const channelKind = normalizeChannelKind(selectedChannel?.kind);
                        setProductResults(rows.filter((product) => productAllowedForChannel(channelKind, product.kind)));

                        setIsLoadingProductSearch(false);
                    })
                    .catch((error) => {
                        console.error(error);
                        setProductResults([]); // Evita que o menu fique aberto com erro
                        setIsLoadingProductSearch(false);
                    });
            }, 1000);
        } else {
            setProductResults([]); // Limpa os resultados se o nome for menor que 3 caracteres
        }
    };

    const handleCloseProductMenu = () => {
        setAnchorElProduct(null);
        setProductResults([]);
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link onClick={() => navigate(ordersReturnPath)} to={ordersReturnPath}>
                    Pedidos
                </Link>
                <Typography color="text.primary">Editar pedido</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar pedido</Typography>
            {orderRecord ? (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <OrderStagesPanel
                        order={orderRecord}
                        onOrderUpdated={setOrderRecord}
                    />
                </Box>
            ) : null}
            <form onSubmit={(e) => handleSubmit(e)}>
                <Grid item xs={12} sm={12}>Canal de vendas</Grid>
                <Box mb={2} />
                <SalesChannelSelect
                    label="Por onde a venda está sendo feita"
                    value={salesChannel}
                    onChange={(e) => setSalesChannel(e.target.value)}
                    onChannelChange={setSelectedChannel}
                />
                {resultErrors.sales_channel && (
                    <div
                        className="text-danger mt-1"
                        style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                    >
                        {resultErrors.sales_channel}
                    </div>
                )}
                {selectedChannel?.requires_external_order_id === true && (
                    <>
                        <Box mb={2} />
                        <TextField
                            label={externalOrderIdLabel(selectedChannel?.name)}
                            fullWidth
                            value={externalOrderId}
                            onChange={(e) => setExternalOrderId(e.target.value)}
                        />
                        {resultErrors.external_order_id && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.external_order_id}
                            </div>
                        )}
                        <Box mb={2} />
                        <TextField
                            label="URL do pedido no painel (opcional)"
                            fullWidth
                            value={externalOrderUrl}
                            onChange={(e) => setExternalOrderUrl(e.target.value)}
                            placeholder="https://seller.shopee.com.br/portal/sale/order/…"
                            helperText="Cole o link dos detalhes do pedido no painel do canal, se quiser abrir depois pela lista."
                        />
                        {resultErrors.external_order_url && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.external_order_url}
                            </div>
                        )}
                    </>
                )}
                <Box mb={6} />
                <Grid item xs={12} sm={12}>
                    <Typography variant="h5">Dados do cliente</Typography>
                </Grid>
                <Box mb={2} />
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="customer-name"
                            label={isLoadingCustomerSearch ? 'Buscando cliente...' : 'Nome (obrigatório)'}
                            fullWidth
                            value={customerName}
                            onChange={(e) => {
                                setCustomerName(e.target.value);
                                customerSearch(e.target.value);
                            }}
                            onFocus={(e) => setAnchorElCustomer(e.currentTarget)} // Aqui está a correção!
                        />
                        <Menu
                            anchorEl={anchorElCustomer}
                            open={Boolean(anchorElCustomer) && customerResults.length > 0}
                            onClose={handleCloseCustomerMenu} // Fecha o menu ao clicar fora
                            disableAutoFocusItem
                        >
                            {customerResults.map((customer) => (
                                <MenuItem
                                    key={customer.uuid || customer.id}
                                    onClick={() => {
                                        setCustomerName(customer.name);
                                        setCustomerEmail(customer.email || '');
                                        setCustomerPhone(formatPhone(customer.phone));
                                        setCustomerId(customer.uuid || customer.id || '');
                                        handleCloseCustomerMenu();
                                    }}
                                >
                                    {applyName(customer.name)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {resultErrors.customer_name && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.customer_name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <PhoneInput
                            label="Telefone"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="E-mail"
                            fullWidth
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            type="email"
                        />
                        {resultErrors.customer_email && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.customer_email}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>

                    {/* Exibir imagem se existir */}
                    {checkoutImage && (
                        <Grid item xs={12} sm={12}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                marginBottom: 3,
                                padding: 2,
                                // backgroundColor: '#f5f5f5',
                                borderRadius: 2,
                                border: '1px solid #bbb'
                            }}>
                                <Typography variant="h6" sx={{ marginBottom: 2, color: '#666' }}>
                                    Produtos nesta venda
                                </Typography>
                                <img
                                    src={checkoutImage}
                                    alt="Foto dos produtos do checkout"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Produtos</Typography>
                        {productFilterHint(selectedChannel?.kind) ? (
                            <Typography variant="body2" color="text.secondary">{productFilterHint(selectedChannel?.kind)}</Typography>
                        ) : null}
                    </Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={isLoadingProductSearch ? 'Buscando produto...' : 'Nome do produto (obrigatório)'}
                            fullWidth
                            value={productName}
                            onChange={(e) => {
                                setProductName(e.target.value);
                                productSearch(e.target.value);
                            }}
                            onFocus={(e) => setAnchorElProduct(e.currentTarget)} // Aqui está a correção!
                        />
                        <Menu
                            anchorEl={anchorElProduct}
                            open={Boolean(anchorElProduct) && productResults.length > 0}
                            onClose={handleCloseProductMenu} // Fecha o menu ao clicar fora
                            disableAutoFocusItem
                        >
                            {productResults.map((product) => (
                                <MenuItem
                                    key={product.uuid || product.id}
                                    onClick={() => {
                                        setProductName(product.name);
                                        setProductUnitPrice((product.base_price / 100));
                                        setProductQuantity(1);
                                        setProductId(product.uuid || product.id);
                                        setProductKind(product.kind || '');
                                        handleCloseProductMenu();
                                    }}
                                >
                                    {product.name} — {formatCurrencyInCents(product.base_price)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {addProductErrors.product_name && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <MoneyInput
                            label="Valor unitário"
                            value={productUnitPrice}
                            onChange={(newValue) => setProductUnitPrice(newValue)} // Passa apenas o valor numérico bruto
                        />
                        {addProductErrors.product_price && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Quantidade"
                            fullWidth
                            value={productQuantity}
                            onChange={(e) => setProductQuantity(e.target.value)}
                            inputMode="numeric"
                        />
                        {addProductErrors.product_quantity && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_quantity}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddProduct}
                            startIcon={<Add />}
                            fullWidth
                        >
                            Incluir este produto
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={12}>Produtos Adicionados</Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={12}>
                        <ul>
                            {products.map((product, index) => (
                                <li key={index}>
                                    {product.name} - {product.quantity} x {formatCurrencyInCents(product.base_price)} = {formatCurrencyInCents(product.total)}
                                    <IconButton onClick={() => handleRemoveProduct(index)}>
                                        <Delete color="error" />
                                    </IconButton>
                                </li>
                            ))}
                        </ul>
                        {resultErrors.products && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.products}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    {fromQuote && (
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Condições combinadas no orçamento — escolha frete e pagamento abaixo conforme o cliente.
                                </Typography>
                                {(Array.isArray(fromQuote.shipping_options) ? fromQuote.shipping_options : []).length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Frete</Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {fromQuote.shipping_options.map((option, index) => (
                                                <li key={`quote-ship-${index}`}>
                                                    <Typography variant="body2">{formatQuoteShippingOption(option)}</Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {(Array.isArray(fromQuote.payment_options) ? fromQuote.payment_options : []).length > 0 && (
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Pagamento</Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {fromQuote.payment_options.map((option, index) => (
                                                <li key={`quote-pay-${index}`}>
                                                    <Typography variant="body2">{formatQuotePaymentOption(option)}</Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {(Number(fromQuote.discount) || 0) > 0 && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Desconto no orçamento: {formatCurrencyInCents(fromQuote.discount)}
                                        {fromQuote.discount_reason ? ` (${fromQuote.discount_reason})` : ''}
                                    </Typography>
                                )}
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Desconto e comissão</Typography>
                    </Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={4}>
                        <MoneyInputWithBlur
                            label="Desconto"
                            value={discount}
                            onChange={(values) => setDiscount(values.value === '' ? 0 : values.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Motivo do desconto"
                            fullWidth
                            value={discountReason}
                            onChange={(e) => setDiscountReason(e.target.value)}
                            helperText={discountCents > 0 ? 'Obrigatório quando há desconto' : ' '}
                        />
                        {resultErrors.discount_reason && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.discount_reason}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MoneyInputWithBlur
                            label="Comissão (opcional)"
                            value={commission}
                            onChange={(values) => setCommission(values.value === '' ? 0 : values.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Dados de pagamento</Typography>
                    </Grid>
                    <Box mb={2} />

                    {!channelForcesPaid(selectedChannel?.kind) ? (
                    <Grid item xs={12} sm={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={customerWillPayLater}
                                    onChange={handleCheckboxChange}
                                    color="primary"
                                />
                            }
                            label="O pagamento desta venda será realizado em outro momento"
                        />
                    </Grid>
                    ) : (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Pagamento já realizado na plataforma. O repasse aparece como &quot;a liberar&quot; até a liberação.
                        </Typography>
                    </Grid>
                    )}
                    {!customerWillPayLater && (
                        <Grid item xs={12}>
                            <CombinedPaymentsEditor
                                payments={payments}
                                onChange={setPayments}
                                totalAmountCents={effectivePayableAmount}
                                defaultDate={new Date()}
                            />
                        </Grid>
                    )}

                    {resultErrors.payment_methods && (
                        <Grid item xs={12} sm={12}>
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.payment_methods}
                            </div>
                        </Grid>
                    )}

                    {channelNeedsShipping(selectedChannel?.kind) ? (
                        <>
                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Dados de entrega</Typography>
                    </Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={4}>
                        <ShippingMethodsSelect
                            label="Forma de entrega"
                            value={shippingMethod}
                            onChange={(e) => setShippingMethod(e.target.value)}
                        />
                        {resultErrors.shipping_method && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.shipping_method}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MoneyInputWithBlur
                            label="Informe o valor do frete"
                            value={shippingCost}
                            onChange={(values) => setShippingCost(values.value)}
                            onBlur={handleUpdateAmount}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Prazo em dias"
                            fullWidth
                            value={shippingDeadline}
                            onChange={(e) => setShippingDeadline(e.target.value)}
                            inputMode="numeric"
                        />
                    </Grid>
                        </>
                    ) : null}
                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            label="Observações"
                            fullWidth
                            multiline
                            minRows={3}
                            value={observations}
                            onChange={(e) => setObservations(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        {checkoutImage && (
                            <Typography variant="h4" align="center">Valor total registrado pelo Quick Checkout: {formatCurrencyInCents(receivedAmount)}</Typography>
                        )}
                        <Typography variant="h3" align="center">Total: {formatCurrencyInCents(effectivePayableAmount)}</Typography>
                        {discountCents > 0 && (
                            <Typography variant="body2" align="center" color="text.secondary">
                                Bruto: {formatCurrencyInCents(effectiveGrossAmount)} · Desconto: {formatCurrencyInCents(discountCents)}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Empilha no mobile, lado a lado em telas maiores
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 2,
                        marginBottom: 8,
                        gap: 2 // Espaçamento entre os itens
                    }}
                >
                    {/* Mensagem de erro à esquerda */}
                    {resultErrors.has_errors ? (
                        <div
                            className="text-danger"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            Ocorreram erros de validação, verifique os campos acima.
                        </div>
                    ) : (
                        <div style={{ height: '1rem' }} />
                    )}

                    {/* Botões responsivos */}
                    <Grid container spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                                onClick={(e) => handleSubmit(e)}
                                fullWidth
                            >
                                {isSubmitting ? 'Salvando...' : 'Salvar pedido'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </form>
        </div>
    );
}

export default EditCheckoutPage;
